class NerdshopTabs extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.tabs = Array.from(this.querySelectorAll('[data-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-tab-panel]'));
    if (!this.tabs.length) return;
    this.querySelector('[data-tab-list]').setAttribute('role', 'tablist');
    this.tabs.forEach((tab, index) => {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', tab.dataset.tab);
      const panel = this.panels.find(item => item.id === tab.dataset.tab);
      if (panel) {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        panel.tabIndex = 0;
      }
      tab.addEventListener('click', event => {
        event.preventDefault();
        this.select(index);
      });
      tab.addEventListener('keydown', event => {
        const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
        if (!keys.includes(event.key)) return;
        event.preventDefault();
        let next = index;
        if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        this.select(next);
        this.tabs[next].focus();
      });
    });
    this.select(0);
  }

  select(index) {
    this.tabs.forEach((tab, tabIndex) => {
      tab.setAttribute('aria-selected', String(tabIndex === index));
      tab.tabIndex = tabIndex === index ? 0 : -1;
    });
    this.panels.forEach(panel => {
      panel.hidden = panel.id !== this.tabs[index].dataset.tab;
      if (!panel.hidden) {
        // A carousel measured in a hidden tab needs its visible width again.
        requestAnimationFrame(() => {
          panel.querySelectorAll('carousel-slider').forEach(slider => {
            if (slider.slider) slider.handleBreakpointChange();
          });
        });
      }
    });
    this.querySelectorAll('[data-tab-collection-link]').forEach(link => {
      link.hidden = link.dataset.tabCollectionLink !== this.tabs[index].dataset.tab;
    });
  }
}

if (!customElements.get('nerdshop-tabs')) customElements.define('nerdshop-tabs', NerdshopTabs);

class NerdshopCategoryMosaic extends HTMLElement {
  connectedCallback() {
    this.captions = Array.from(this.querySelectorAll('.ns-category-mosaic__caption'));
    this.schedule = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.alignCaptions());
    };
    this.resizeObserver = new ResizeObserver(this.schedule);
    this.resizeObserver.observe(this);
    this.captions.forEach(caption => this.resizeObserver.observe(caption.firstElementChild));
    document.fonts?.ready.then(() => { if (this.isConnected) this.schedule(); });
    this.schedule();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
    this.resizeObserver?.disconnect();
  }

  alignCaptions() {
    if (!this.captions.length || !this.clientWidth) return;
    // Measure the text, not the shared caption height, so shorter labels can shrink again.
    const height = Math.ceil(Math.max(...this.captions.map(caption => {
      const styles = getComputedStyle(caption);
      return caption.firstElementChild.getBoundingClientRect().height + parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
    })));
    const value = `${height}px`;
    if (this.style.getPropertyValue('--mosaic-caption-height') !== value) {
      this.style.setProperty('--mosaic-caption-height', value);
    }
  }
}

if (!customElements.get('nerdshop-category-mosaic')) customElements.define('nerdshop-category-mosaic', NerdshopCategoryMosaic);

function nerdshopRelativeLuminance(red, green, blue) {
  const channels = [red, green, blue].map(value => {
    const channel = value / 255;
    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
}

function nerdshopBreadcrumbContrast(luminances) {
  if (!luminances.length) return 'adaptive';
  const ink = nerdshopRelativeLuminance(16, 17, 23);
  let darkContrast = Infinity;
  let lightContrast = Infinity;
  luminances.forEach(luminance => {
    darkContrast = Math.min(darkContrast, (Math.max(luminance, ink) + 0.05) / (Math.min(luminance, ink) + 0.05));
    lightContrast = Math.min(lightContrast, 1.05 / (luminance + 0.05));
  });
  if (darkContrast < 4.5 && lightContrast < 4.5) return 'adaptive';
  return darkContrast >= lightContrast ? 'dark-text' : 'light-text';
}

class NerdshopBreadcrumbContrast extends HTMLElement {
  connectedCallback() {
    this.media = this.closest('.product-media');
    if (!this.media) return;
    this.schedule = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.updateContrast());
    };
    this.media.addEventListener('load', this.schedule, true);
    this.media.addEventListener('scroll', this.schedule, { capture: true, passive: true });
    this.resizeObserver = new ResizeObserver(this.schedule);
    this.resizeObserver.observe(this.media);
    this.resizeObserver.observe(this);
    this.mutationObserver = new MutationObserver(this.schedule);
    this.mutationObserver.observe(this.media, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'srcset', 'hidden', 'class']
    });
    this.schedule();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
    this.media?.removeEventListener('load', this.schedule, true);
    this.media?.removeEventListener('scroll', this.schedule, true);
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  updateContrast() {
    const bounds = this.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    const images = Array.from(this.media.querySelectorAll('.media-viewer__item:not([hidden]) .product-image')).filter(image => {
      const rect = image.getBoundingClientRect();
      return rect.right > bounds.left && rect.left < bounds.right && rect.bottom > bounds.top && rect.top < bounds.bottom;
    });
    if (!images.length || images.some(image => !image.complete || !image.naturalWidth)) {
      this.dataset.contrast = 'adaptive';
      return;
    }
    try {
      // CDN images without CORS cannot be sampled; CSS still follows their visible pixels.
      if (images.some(image => !image.crossOrigin && new URL(image.currentSrc || image.src, location.href).origin !== location.origin)) {
        this.dataset.contrast = 'adaptive';
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = 64;
      canvas.height = Math.max(8, Math.min(32, Math.ceil(64 * bounds.height / bounds.width)));
      const context = canvas.getContext('2d', { willReadFrequently: true });
      context.scale(canvas.width / bounds.width, canvas.height / bounds.height);
      context.fillStyle = '#fff';
      context.fillRect(0, 0, bounds.width, bounds.height);
      const ancestors = [];
      for (let element = this.parentElement; element; element = element.parentElement) ancestors.unshift(element);
      ancestors.forEach(element => {
        context.fillStyle = getComputedStyle(element).backgroundColor;
        context.fillRect(0, 0, bounds.width, bounds.height);
      });
      images.forEach(image => {
        const rect = image.getBoundingClientRect();
        const styles = getComputedStyle(image);
        let width = rect.width;
        let height = rect.height;
        if (styles.objectFit === 'contain' || styles.objectFit === 'cover') {
          const scale = (styles.objectFit === 'contain' ? Math.min : Math.max)(rect.width / image.naturalWidth, rect.height / image.naturalHeight);
          width = image.naturalWidth * scale;
          height = image.naturalHeight * scale;
        }
        const position = styles.objectPosition.split(' ');
        const offset = (value, space) => value.endsWith('%') ? space * parseFloat(value) / 100 : parseFloat(value) || 0;
        const left = rect.left + offset(position[0], rect.width - width);
        const top = rect.top + offset(position[1] || '50%', rect.height - height);
        context.save();
        context.beginPath();
        context.rect(rect.left - bounds.left, rect.top - bounds.top, rect.width, rect.height);
        context.clip();
        const imageAncestors = [];
        for (let element = image; element && element !== this.media; element = element.parentElement) imageAncestors.unshift(element);
        imageAncestors.forEach(element => {
          context.fillStyle = getComputedStyle(element).backgroundColor;
          context.fillRect(rect.left - bounds.left, rect.top - bounds.top, rect.width, rect.height);
        });
        context.drawImage(image, left - bounds.left, top - bounds.top, width, height);
        context.restore();
      });
      const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
      const luminances = [];
      for (let index = 0; index < pixels.length; index += 4) {
        luminances.push(nerdshopRelativeLuminance(pixels[index], pixels[index + 1], pixels[index + 2]));
      }
      this.dataset.contrast = nerdshopBreadcrumbContrast(luminances);
    } catch {
      this.dataset.contrast = 'adaptive';
    }
  }
}

if (!customElements.get('nerdshop-breadcrumb-contrast')) customElements.define('nerdshop-breadcrumb-contrast', NerdshopBreadcrumbContrast);

document.addEventListener('keydown', event => {
  const viewport = event.target.closest('.ns-product-carousel .slider');
  if (!viewport || event.target !== viewport || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
  event.preventDefault();
  const carousel = viewport.closest('carousel-slider');
  const distance = carousel.slideSpan || viewport.clientWidth;
  const rtl = document.dir === 'rtl' ? -1 : 1;
  const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
  if (event.key === 'Home' || event.key === 'End') {
    viewport.scrollTo({ left: event.key === 'Home' ? 0 : rtl * viewport.scrollWidth, behavior });
  } else {
    viewport.scrollBy({ left: (event.key === 'ArrowRight' ? 1 : -1) * distance, behavior });
  }
});

function initializeNerdshopIndex(root = document) {
  root.querySelectorAll('.ns-index').forEach(index => {
    const input = index.querySelector('[data-universe-input]');
    if (!input || input.dataset.initialized) return;
    input.dataset.initialized = 'true';
    const search = index.querySelector('[data-universe-search]');
    if (search) search.hidden = false;
    const normalize = value => value.toLocaleLowerCase('da-DK').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/æ/g, 'ae').replace(/ø/g, 'o').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
    const items = Array.from(index.querySelectorAll('[data-universe-item]'));
    const groups = Array.from(index.querySelectorAll('[data-universe-group]'));
    const letters = Array.from(index.querySelectorAll('[data-universe-jump]'));
    const update = () => {
      const searchTerm = normalize(input.value.trim());
      const query = ['d d', 'dnd'].includes(searchTerm) ? 'dungeons dragons' : searchTerm;
      const terms = query.split(/\s+/).filter(Boolean);
      const abbreviation = query.replace(/\s/g, '');
      const initialsOnly = terms.length > 1 && terms.every(term => term.length === 1);
      let matches = 0;
      items.forEach(item => {
        const keywords = normalize(item.dataset.universeKeywords || item.textContent);
        const initials = normalize(item.dataset.universeTitle || item.textContent).split(/\s+/).filter(word => word && !['and', 'og', 'the', 'all'].includes(word)).map(word => word[0]).join('');
        const matchesSearch = initialsOnly ? abbreviation === initials : terms.every(term => keywords.includes(term)) || (abbreviation.length > 1 && abbreviation === initials);
        item.hidden = !matchesSearch;
        if (!item.hidden) matches += 1;
      });
      groups.forEach(group => {
        group.hidden = !Array.from(group.querySelectorAll('[data-universe-item]')).some(item => !item.hidden);
      });
      letters.forEach(link => {
        link.hidden = !groups.some(group => group.dataset.universeLetter === link.dataset.universeJump && !group.hidden);
      });
      const empty = index.querySelector('[data-universe-empty]');
      if (empty) empty.hidden = matches > 0;
    };
    input.addEventListener('input', update);
    update();
  });
}

document.addEventListener('DOMContentLoaded', () => initializeNerdshopIndex());
document.addEventListener('shopify:section:load', event => initializeNerdshopIndex(event.target));

// Native collection facets become dismissible dropdowns on desktop only.
const nerdshopFilterSelector = '.ns-collection-top-filters .facets__filters details.filter';
function positionNerdshopFilter(filter) {
  const panel = filter.querySelector('.disclosure__panel');
  panel.style.removeProperty('--ns-filter-offset');
  const bounds = panel.getBoundingClientRect();
  const offset = Math.max(20 - bounds.left, Math.min(0, innerWidth - 20 - bounds.right));
  panel.style.setProperty('--ns-filter-offset', `${offset}px`);
}
document.addEventListener('toggle', event => {
  if (!matchMedia('(min-width: 1024px)').matches || !event.target.matches(nerdshopFilterSelector) || !event.target.open) return;
  event.target.closest('.facets__filters').querySelectorAll('details.filter[open]').forEach(filter => {
    if (filter !== event.target) filter.open = false;
  });
  positionNerdshopFilter(event.target);
}, true);
function repositionNerdshopFilters() {
  if (matchMedia('(min-width: 1024px)').matches) document.querySelectorAll(`${nerdshopFilterSelector}[open]`).forEach(positionNerdshopFilter);
}
window.addEventListener('on:debounced-resize', repositionNerdshopFilters);
document.addEventListener('on:facet-filters:updated', repositionNerdshopFilters);
function syncNerdshopCollectionCount() {
  const source = document.querySelector('#filter-results [data-ns-collection-count]');
  const target = document.querySelector('.ns-collection-header__count');
  if (source && target) target.textContent = source.textContent;
}
document.addEventListener('DOMContentLoaded', syncNerdshopCollectionCount);
document.addEventListener('on:facet-filters:updated', syncNerdshopCollectionCount);
document.addEventListener('shopify:section:load', syncNerdshopCollectionCount);
document.addEventListener('click', event => {
  if (!matchMedia('(min-width: 1024px)').matches || event.target.closest(nerdshopFilterSelector)) return;
  document.querySelectorAll(`${nerdshopFilterSelector}[open]`).forEach(filter => { filter.open = false; });
});
document.addEventListener('keydown', event => {
  if (event.key !== 'Escape' || !matchMedia('(min-width: 1024px)').matches) return;
  const filter = event.target.closest(`${nerdshopFilterSelector}[open]`);
  if (!filter) return;
  event.preventDefault();
  filter.open = false;
  filter.querySelector('summary').focus();
});
